import { sanitizeSegment } from "../../storage/fsa.ts";

/**
 * Output tree for OneDrive (ARCHITECTURE.md §7): `onedrive/<original path preserved>`.
 *
 * Unlike Mail (which had to walk parentFolderId chains client-side to build a
 * path — Graph's default folder listing doesn't return one) or Teams (whose
 * chat "path" is a synthesized label plus a disambiguating hash), a OneDrive
 * delta item already carries its resolved parent path server-side, computed
 * by Graph itself rather than reconstructed here. There is nothing to build,
 * only to parse.
 */

const ROOT_MARKER = "root:";

/**
 * Extracts the folder segments from `parentReference.path`
 * ("/drive/root:/Documents/Projects" → ["Documents", "Projects"]).
 *
 * Returns `[]` for the drive root itself, and — defensively — for a path that
 * doesn't contain the expected "root:" marker at all, rather than throwing;
 * an item that can't be placed precisely still lands somewhere sane (directly
 * under `onedrive/`) instead of failing the whole item.
 */
export function relativeFolderSegments(parentPath: string | undefined): string[] {
    if (!parentPath) {
        return [];
    }
    const markerIndex = parentPath.indexOf(ROOT_MARKER);
    if (markerIndex === -1) {
        return [];
    }
    const rest = parentPath.slice(markerIndex + ROOT_MARKER.length);
    return rest.split("/").filter((s) => s.length > 0);
}

export function folderDiskSegments(segments: readonly string[], name: string): string[] {
    return ["onedrive", ...segments.map((s) => sanitizeSegment(s)), sanitizeSegment(name)];
}

export function fileRelativePath(segments: readonly string[], name: string): string {
    return folderDiskSegments(segments, name).join("/");
}

/** Human-readable relative path, for progress/log display — not sanitized for disk. */
export function displayPath(segments: readonly string[], name: string): string {
    return [...segments, name].join("/");
}

export const ONEDRIVE_DIR = "onedrive";
